/*
 * gmx-product.js — behaviour for sections/gmx-product.liquid (the Ponies PDP).
 *
 * Five jobs, all vanilla:
 *   1. Gallery — a CSS scroll-snap carousel: swipe on touch, thumbnails and
 *      dots stay in sync with the scroll position, thumbnails scroll the track.
 *   2. Description — clamp long copy to four lines behind a "Read more" toggle.
 *   3. Quantity — the ± buttons, and the live "Add to cart · $total" label.
 *   4. Variants — the option pills set the posted variant id and the price.
 *   5. Add to cart — real POST to /cart/add.js, the design's 1.8s confirmation
 *      state, then the header cart badge is refreshed from /cart.js.
 *   6. Accordions — animated open/close for the <details> blocks.
 *   7. Volume tiers — lights the tier the cart has reached. The count
 *      itself comes from assets/gmx-discounts.js (event `gmx:discount-state`),
 *      which owns every cart-driven price and discount statement on the page.
 *
 * The glitter picker (CustomMarx kits) writes its selection into the hidden
 * input `properties[Glitter colors]`, which posts with the same form.
 */
(function () {
  "use strict";

  var ADDED_MS = 1800;

  /* ---------------------------------------------------------------- helpers */

  /* Whole amounts print as "$48", not "$48.00" — matches the Liquid side, which
     uses the money_without_trailing_zeros filter. */
  function money(cents) {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    var whole = cents % 100 === 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: whole ? 0 : 2,
      }).format(cents / 100);
    } catch (e) {
      return "$" + (whole ? String(cents / 100) : (cents / 100).toFixed(2));
    }
  }

  function updateCartBadge(count) {
    document.querySelectorAll("[data-gmx-cart-count]").forEach(function (el) {
      el.textContent = count;
      el.setAttribute("data-count", count);
    });
  }

  function refreshCartBadge() {
    return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart.js" : "/cart.js", {
      headers: { Accept: "application/json" },
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        updateCartBadge(cart.item_count);
        document.dispatchEvent(new CustomEvent("gmx:cart:updated", { detail: { cart: cart } }));
        return cart;
      })
      .catch(function () {
        /* badge stays as-is if the cart read fails */
        return null;
      });
  }

  /* ---------------------------------------------------------------- gallery */

  /* The main image is a CSS scroll-snap track, so the browser owns the swipe
     and the scroll position is the only source of truth. This wires the two
     directions: scrolling lights the matching thumbnail and dot, and tapping a
     thumbnail scrolls the track to that slide. Desktop behaviour is unchanged
     from the shopper's side — clicking a thumbnail still shows that image. */
  function initGallery(root) {
    var scroller = root.querySelector("[data-gmx-gallery-scroller]");
    if (!scroller) return;

    var slides = Array.prototype.slice.call(scroller.querySelectorAll("[data-gmx-gallery-slide]"));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll("[data-gmx-gallery-thumb]"));
    var dots = Array.prototype.slice.call(root.querySelectorAll("[data-gmx-gallery-dot]"));
    if (slides.length < 2) return;

    var current = 0;

    function paint(index) {
      if (index === current) return;
      current = index;
      thumbs.forEach(function (thumb, i) {
        var on = i === index;
        thumb.classList.toggle("is-active", on);
        thumb.setAttribute("aria-pressed", on ? "true" : "false");
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle("is-active", i === index);
      });
    }

    /* Read the index from the scroll offset rather than IntersectionObserver:
       one number, no thresholds, and it is correct mid-swipe as well as at rest. */
    var ticking = false;
    scroller.addEventListener(
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var width = scroller.clientWidth || 1;
          var index = Math.round(scroller.scrollLeft / width);
          paint(Math.max(0, Math.min(slides.length - 1, index)));
        });
      },
      { passive: true },
    );

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () {
        scroller.scrollTo({ left: i * scroller.clientWidth, behavior: "smooth" });
        paint(i);
      });
    });
  }

  /* ------------------------------------------------------- description clamp */

  /* Long product copy clamps to four lines with a "Read more" toggle. The
     toggle is only revealed when the clamped body actually overflows, so short
     descriptions render as plain paragraphs. Re-measured on resize (and once
     the webfonts land, since fallback metrics fake extra line wraps) unless the
     shopper has already expanded it. */
  function initDescription(root) {
    var wrap = root.querySelector("[data-gmx-desc]");
    if (!wrap) return;

    var body = wrap.querySelector("[data-gmx-desc-body]");
    var btn = wrap.querySelector("[data-gmx-desc-toggle]");
    var label = wrap.querySelector("[data-gmx-desc-toggle-label]");
    if (!body || !btn || !label) return;

    /* Several descriptions were pasted into admin with blank paragraphs (a lone
       non-breaking space) at the start or end. They render as empty lines and
       waste one of the four clamped lines, so drop them. */
    Array.prototype.slice.call(body.children).forEach(function (child) {
      if (!child.textContent.replace(/\u00a0/g, " ").trim() && !child.querySelector("img, iframe, video")) {
        child.remove();
      }
    });

    function measure() {
      if (wrap.classList.contains("is-open")) return;
      wrap.classList.add("is-clamped");
      var overflows = body.scrollHeight - body.clientHeight > 4;
      wrap.classList.toggle("is-clamped", overflows);
      btn.hidden = !overflows;
    }

    btn.addEventListener("click", function () {
      var open = wrap.classList.toggle("is-open");
      wrap.classList.toggle("is-clamped", !open);
      btn.setAttribute("aria-expanded", open ? "true" : "false");
      label.textContent = open ? "Read less" : "Read more";
    });

    measure();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure);
    }

    var resizeTimer = null;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });
  }

  /* ------------------------------------------------------------ glitter pick */

  /* The picker's own behaviour (swatch toggle, chips, gmxValidate hook) lives
     in assets/gmx-glitter-picker.js and self-initializes independently. This
     file's buy-box submit handler just queries `[data-gmx-glitter-picker]`
     and calls `.gmxValidate()` on it — see initBuyBox below. */

  /* -------------------------------------------------------- Judge.me reviews */

  /* Everything here reads Judge.me's own markup. The theme never writes a
     rating, a review count or a review. Two numbers are available:
       - the product's score, in the preview badge's data attributes;
       - the store's score, in the review widget's data attributes.
     They are labelled differently in the UI so a store average is never shown
     as if it were this product's rating. */

  function jdgmWidget() {
    return document.querySelector("#judgeme_product_reviews, .jdgm-review-widget");
  }

  function num(el, attr) {
    if (!el) return 0;
    var v = parseFloat(el.getAttribute(attr));
    return isFinite(v) ? v : 0;
  }

  function plural(n, word) {
    return n + " " + word + (n === 1 ? "" : "s");
  }

  function initRating(root) {
    var shopEl = root.querySelector("[data-gmx-shop-rating]");
    if (!shopEl) return;

    function fill() {
      var badge = root.querySelector(".jdgm-prev-badge");
      var productReviews = num(badge, "data-number-of-reviews");
      if (productReviews > 0) {
        shopEl.hidden = true;
        return true;
      }
      var widget = jdgmWidget();
      var shopCount = num(widget, "data-shop-review-count");
      var shopAvg = num(widget, "data-shop-average-rating");
      if (!widget || shopCount <= 0) return !!widget;
      /* Explicitly "store", never this product's own score. */
      shopEl.textContent = shopAvg.toFixed(1) + " ★ · " + plural(shopCount, "store review");
      shopEl.hidden = false;
      return true;
    }

    if (!fill()) poll(fill, 12000);
  }

  /* Judge.me boots asynchronously; poll until its markup exists, then stop. */
  function poll(fn, ms) {
    var started = Date.now();
    var timer = setInterval(function () {
      if (fn() || Date.now() - started > ms) clearInterval(timer);
    }, 400);
  }

  function initReviews(root) {
    var band = root.querySelector("#gmx-reviews");
    if (!band) return;

    /* Judge.me's own widget renders the same reviews in the app's own layout.
       gmx-product.css hides its section with `:has()`; this is the fallback for
       browsers without `:has()`. The guard means a widget that ever ends up
       inside the product section itself can never hide the whole page. */
    function hideJudgeMeWidget() {
      var widget = document.querySelector("#judgeme_product_reviews, .jdgm-widget.jdgm-review-widget");
      if (!widget) return false;
      var host = widget.closest(".shopify-section");
      if (host && !host.classList.contains("section-gmx-product")) host.classList.add("gmx-jdgm-host");
      return true;
    }
    if (!hideJudgeMeWidget()) poll(hideJudgeMeWidget, 12000);

    var track = band.querySelector("[data-gmx-reviews-track]");
    var arrows = band.querySelector("[data-gmx-reviews-arrows]");
    if (!track || !arrows) return;

    /* Arrows exist only while the row actually overflows: one card on a wide
       screen has nowhere to scroll to, so they stay away. */
    function sizeArrows() {
      arrows.hidden = track.scrollWidth - track.clientWidth < 8;
    }

    sizeArrows();
    window.addEventListener("resize", sizeArrows);
    if (window.ResizeObserver) new ResizeObserver(sizeArrows).observe(track);
    /* Web fonts land after first paint and change how wide the cards' text is. */
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sizeArrows);

    arrows.querySelectorAll("[data-gmx-reviews-scroll]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var dir = parseInt(btn.getAttribute("data-gmx-reviews-scroll"), 10) || 1;
        track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: "smooth" });
      });
    });
  }

  /* ----------------------------------------------------------- accordions */

  /* <details> snaps: the browser shows or hides the panel in one frame, which
     shoves everything under it. So the JS takes the state over.
     Opening : set [open] first (the panel needs height to grow into), then add
               .is-open on the next frame so 0fr -> 1fr actually transitions.
     Closing : drop .is-open, wait for the row transition, then clear [open].
     Without JS the CSS leaves [open] panels fully expanded, so the markup
     still works. Reduced motion skips straight to the end state. */
  function initAccordions(root) {
    var group = root.querySelector(".gmx-pdp__acc");
    if (!group) return;

    var items = Array.prototype.slice.call(group.querySelectorAll("details.gmx-pdp-acc__item"));
    if (!items.length) return;

    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    group.classList.add("is-enhanced");
    items.forEach(function (item) {
      if (item.open) item.classList.add("is-open");
    });

    function close(item) {
      var panel = item.querySelector(".gmx-pdp-acc__panel");
      item.classList.remove("is-open");
      if (reduced || !panel) {
        item.open = false;
        return;
      }
      clearTimeout(item.gmxAccTimer);
      var done = function (event) {
        if (event && event.propertyName !== "grid-template-rows") return;
        panel.removeEventListener("transitionend", done);
        clearTimeout(item.gmxAccTimer);
        if (!item.classList.contains("is-open")) item.open = false;
      };
      panel.addEventListener("transitionend", done);
      /* transitionend never fires on a hidden tab, so always keep a backstop. */
      item.gmxAccTimer = setTimeout(done, 600);
    }

    function open(item) {
      clearTimeout(item.gmxAccTimer);
      item.open = true;
      if (reduced) {
        item.classList.add("is-open");
        return;
      }
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          item.classList.add("is-open");
        });
      });
    }

    items.forEach(function (item) {
      var head = item.querySelector("summary");
      if (!head) return;
      head.addEventListener("click", function (event) {
        /* The browser would toggle [open] itself, instantly. */
        event.preventDefault();
        if (item.classList.contains("is-open")) {
          close(item);
        } else {
          open(item);
        }
      });
    });
  }

  /* ---------------------------------------------------------- volume tiers */

  /* Lights the tier the shopper's current quantity has reached. The discount
     itself is a Shopify automatic discount — nothing here changes any price.

     Crossing UP into a tier the shopper had not reached before gets one beat of
     celebration: the row glows, its saving chip pops in, three sparkles rise,
     and the price line flashes. Crossing DOWN only clears .is-active — no
     animation, no noise. The steady .is-active state is unchanged, so a
     qualified tier stays lit after the beat ends. */
  var CELEBRATE_MS = 900;

  function flashPrice(root) {
    var priceEl = root.querySelector("[data-gmx-price]");
    if (!priceEl) return;
    priceEl.classList.remove("is-flash");
    /* Forced reflow so a repeat crossing restarts the animation. */
    void priceEl.offsetWidth;
    priceEl.classList.add("is-flash");
    clearTimeout(priceEl.gmxFlashTimer);
    priceEl.gmxFlashTimer = setTimeout(function () {
      priceEl.classList.remove("is-flash");
    }, 720);
  }

  function sparkle(tier) {
    var offsets = [
      [-26, -26],
      [0, -34],
      [26, -22],
    ];
    offsets.forEach(function (offset, i) {
      var spark = document.createElement("span");
      spark.className = "gmx-pdp-tiers__spark";
      spark.setAttribute("aria-hidden", "true");
      spark.style.setProperty("--gmx-spark-x", offset[0] + "px");
      spark.style.setProperty("--gmx-spark-y", offset[1] + "px");
      spark.style.animationDelay = i * 70 + "ms";
      tier.appendChild(spark);
      setTimeout(
        function () {
          spark.remove();
        },
        CELEBRATE_MS + i * 70,
      );
    });
  }

  /* The count that lights a tier is NOT the stepper's number: the Shopify
     discount counts every design unit in the cart, so assets/gmx-discounts.js
     owns the arithmetic and publishes it as `gmx:discount-state`. This function
     keeps only what it is good at — the steady .is-active state and the
     one-beat celebration when a tier is crossed. */
  function initTiers(root) {
    var card = root.querySelector("[data-gmx-tiers]");
    if (!card) return;

    var tiers = Array.prototype.slice.call(card.querySelectorAll("[data-gmx-tier-min]"));
    var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    var reached = -1;

    function celebrate(tier) {
      if (reduced) return;
      tier.classList.remove("is-celebrating");
      void tier.offsetWidth;
      tier.classList.add("is-celebrating");
      clearTimeout(tier.gmxTierTimer);
      tier.gmxTierTimer = setTimeout(function () {
        tier.classList.remove("is-celebrating");
      }, CELEBRATE_MS);
      sparkle(tier);
      flashPrice(root);
    }

    function sync(count, animate) {
      var best = -1;
      tiers.forEach(function (tier, i) {
        var min = parseInt(tier.getAttribute("data-gmx-tier-min"), 10) || 0;
        if (count >= min) best = i;
      });
      tiers.forEach(function (tier, i) {
        tier.classList.toggle("is-active", i === best);
      });
      if (animate && best > reached && best > -1) celebrate(tiers[best]);
      reached = best;
    }

    /* The first state to arrive only records where the shopper starts, so a
       cart that already qualifies does not throw confetti on page load. */
    var primed = false;
    document.addEventListener("gmx:discount-state", function (event) {
      sync(event.detail.pdpCount, primed);
      primed = true;
    });

    /* A state can already exist — gmx-discounts.js publishes as soon as its
       cart read lands, which may be before this runs. */
    var known = window.gmxDiscounts && window.gmxDiscounts.state;
    if (known && known.pdpCount) {
      sync(known.pdpCount, false);
      primed = true;
    } else {
      sync(0, false);
    }
  }

  /* ------------------------------------------------------- buy box + submit */

  function initBuyBox(root) {
    var form = root.querySelector(".gmx-pdp__form");
    if (!form) return;

    var qtyInput = form.querySelector("[data-gmx-qty]");
    var addBtn = form.querySelector("[data-gmx-add]");
    var addLabel = form.querySelector("[data-gmx-add-label]");
    var variantInput = form.querySelector("[data-gmx-variant-id]");
    var priceEl = root.querySelector("[data-gmx-price]");
    var compareEl = root.querySelector("[data-gmx-compare]");
    var errorEl = form.querySelector("[data-gmx-form-error]");
    var idleLabel = addBtn ? addBtn.getAttribute("data-label-idle") : "Add to cart";
    var addedLabel = addBtn ? addBtn.getAttribute("data-label-added") : "Added to cart ✓";
    var addedTimer = null;

    function unitPrice() {
      return parseInt(addBtn.getAttribute("data-unit-price"), 10) || 0;
    }

    function quantity() {
      var n = parseInt(qtyInput.value, 10);
      if (isNaN(n) || n < 1) n = 1;
      return n;
    }

    function syncLabel() {
      if (!addBtn || addBtn.disabled || addedTimer) return;
      addLabel.textContent = idleLabel + " · " + money(unitPrice() * quantity());
    }

    /* quantity */
    if (qtyInput) {
      form.querySelectorAll("[data-gmx-qty-step]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var step = parseInt(btn.getAttribute("data-gmx-qty-step"), 10) || 1;
          qtyInput.value = Math.max(1, quantity() + step);
          syncLabel();
        });
      });
      qtyInput.addEventListener("change", function () {
        qtyInput.value = quantity();
        syncLabel();
      });
      qtyInput.addEventListener("input", syncLabel);
    }

    /* variants */
    var variantBtns = form.querySelectorAll("[data-gmx-variant]");
    variantBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        variantBtns.forEach(function (other) {
          var on = other === btn;
          other.classList.toggle("is-active", on);
          other.setAttribute("aria-checked", on ? "true" : "false");
        });
        variantInput.value = btn.getAttribute("data-id");
        addBtn.setAttribute("data-unit-price", btn.getAttribute("data-price"));
        if (priceEl) priceEl.textContent = btn.getAttribute("data-price-formatted");
        if (compareEl) {
          var compare = btn.getAttribute("data-compare");
          compareEl.textContent = compare || "";
          compareEl.hidden = !compare;
        }
        var available = btn.getAttribute("data-available") === "true";
        addBtn.disabled = !available;
        if (!available) {
          addLabel.textContent = "Sold out";
        } else {
          syncLabel();
        }
      });
    });

    /* add to cart */
    form.addEventListener("submit", function (event) {
      event.preventDefault();
      if (!addBtn || addBtn.disabled) return;

      var picker = root.querySelector("[data-gmx-glitter-picker]");
      var problem = picker && picker.gmxValidate ? picker.gmxValidate() : null;
      if (problem) {
        errorEl.textContent = problem;
        errorEl.hidden = false;
        return;
      }

      errorEl.hidden = true;
      addBtn.classList.add("is-loading");

      fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + "cart/add.js" : "/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || "Could not add to cart.");
            return data;
          });
        })
        .then(function () {
          addBtn.classList.remove("is-loading");
          addBtn.classList.add("is-added");
          addLabel.textContent = addedLabel;
          clearTimeout(addedTimer);
          addedTimer = setTimeout(function () {
            addedTimer = null;
            addBtn.classList.remove("is-added");
            syncLabel();
          }, ADDED_MS);
          return refreshCartBadge();
        })
        .then(function (cart) {
          /* Public hook for anything that reacts to a successful add — the
             cart-icon confetti listens for this. `count` is the cart's total
             item count, or this add's quantity if the cart read failed. */
          var count = cart && typeof cart.item_count === "number" ? cart.item_count : quantity();
          document.dispatchEvent(new CustomEvent("gmx:cart-added", { detail: { count: count } }));
          /* assets/gmx-discounts.js re-reads the cart and repaints prices. */
          document.dispatchEvent(new CustomEvent("gmx:cart-changed"));
        })
        .catch(function (err) {
          addBtn.classList.remove("is-loading");
          errorEl.textContent = err.message || "Could not add to cart.";
          errorEl.hidden = false;
        });
    });

    syncLabel();
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    var root = document.querySelector(".gmx-pdp");
    if (!root) return;
    initGallery(root);
    initDescription(root);
    initBuyBox(root);
    initAccordions(root);
    initTiers(root);
    initRating(root);
    initReviews(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
